import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
} from '@mui/material';
import {
  Phone,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

const Hero: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Box
      id="hero"
      sx={{
        minHeight: { xs: '90vh', md: '100vh' },
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        py: { xs: 4, md: 8 },
        pt: { xs: '120px', md: '140px' },
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <Grid container spacing={{ xs: 3, md: 4 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <MotionBox
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h1"
                sx={{
                  mb: { xs: 2, md: 3 },
                  mt: { xs: '25px', md: 0 },
                  background: 'linear-gradient(45deg, #2c3e50, #8bc34a)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                  lineHeight: { xs: 1.2, md: 1.3 },
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                Your Path to Pain-Free Living
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  mb: { xs: 3, md: 4 },
                  color: 'text.secondary',
                  fontWeight: 400,
                  lineHeight: { xs: 1.5, md: 1.6 },
                  fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                  textAlign: { xs: 'center', md: 'left' },
                  px: { xs: 1, md: 0 },
                }}
              >
                Expert chiropractic care in Melton. Evidence-based treatments for back pain, sports injuries, and holistic wellness.
              </Typography>

              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 1.5, md: 2 }, 
                mb: { xs: 3, md: 4 }, 
                flexWrap: 'wrap',
                justifyContent: { xs: 'center', md: 'flex-start' },
                flexDirection: { xs: 'column', sm: 'row' },
              }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    background: 'linear-gradient(45deg, #8bc34a, #689f38)',
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.2, md: 1.5 },
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    minWidth: { xs: '200px', sm: 'auto' },
                    '&:hover': {
                      background: 'linear-gradient(45deg, #689f38, #558b2f)',
                      transform: 'scale(1.05)',
                    },
                    boxShadow: '0 4px 15px rgba(139, 195, 74, 0.3)',
                  }}
                  onClick={() => navigate('/bookings')}
                >
                  Book Appointment
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.2, md: 1.5 },
                    fontSize: { xs: '0.9rem', md: '1.1rem' },
                    borderColor: '#8bc34a',
                    color: '#8bc34a',
                    minWidth: { xs: '200px', sm: 'auto' },
                    borderWidth: 2,
                    '&:hover': {
                      backgroundColor: '#8bc34a',
                      borderColor: '#8bc34a',
                      color: 'white',
                    },
                  }}
                  startIcon={<Phone />}
                  href="tel:(03) 9743 5510"
                >
                  (03) 9743 5510
                </Button>
              </Box>

              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary', 
                  mb: 2,
                  textAlign: { xs: 'center', md: 'left' },
                  fontSize: { xs: '0.9rem', md: '1rem' },
                }}
              >
                📍 477 High Street, Melton VIC 3337
              </Typography>
            </MotionBox>
          </Grid>

          <Grid item xs={12} md={6}>
            <MotionBox
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  borderRadius: { xs: '20px', md: '25px' },
                  overflow: 'hidden',
                  mb: { xs: 4, md: 6 },
                  mx: { xs: 2, md: 0 },
                  mt: { xs: 3, md: 4 },
                  boxShadow: '0 20px 60px rgba(0,0,0,0.25), 0 8px 32px rgba(139, 195, 74, 0.15)',
                  transform: 'perspective(1000px) rotateY(-5deg)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'perspective(1000px) rotateY(0deg) scale(1.02)',
                    boxShadow: '0 30px 80px rgba(0,0,0,0.3), 0 12px 40px rgba(139, 195, 74, 0.2)',
                  },
                }}
              >
                <img
                  src="/staff/announcement.jpeg"
                  alt="New Opening Hours Announcement"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            </MotionBox>
          </Grid>
        </Grid>

      </Container>

      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '50%',
          height: '100%',
          background: 'linear-gradient(45deg, transparent, rgba(44, 62, 80, 0.1))',
          zIndex: -1,
        }}
      />
    </Box>
  );
};

export default Hero;